import { cx, useTranslations } from "@jugl-web/utils";
import { forwardRef } from "react";
import { PlainButton } from "../../primitives/PlainButton";
import { Popover } from "../../primitives/Popover";
import { ReactComponent as ClearIcon } from "./icons/clear.svg";
import { ReactComponent as SearchIcon } from "./icons/search.svg";
import { ReactComponent as SettingsIcon } from "./icons/settings.svg";
import { BaseInputProps } from "./types";

interface FilledSearchInputProps extends BaseInputProps {
  color: "primary" | "grey" | "white";
  size: "sm" | "md";
}

const colorToClasses: Record<FilledSearchInputProps["color"], string> = {
  primary:
    "bg-primary/5 font-medium text-[#333333] placeholder:text-grey placeholder:font-normal",
  grey: "bg-grey-100 font-medium text-[#333333] placeholder:text-grey placeholder:font-normal",
  white:
    "bg-white font-medium text-[#333333] placeholder:text-grey placeholder:font-normal",
};

const sizeToInputClasses: Record<FilledSearchInputProps["size"], string> = {
  sm: "h-[34px] text-sm pl-10",
  md: "h-10 text-base pl-11",
};

const sizeToIconClasses: Record<FilledSearchInputProps["size"], string> = {
  sm: "w-4 h-4",
  md: "",
};

export const FilledSearchInput = forwardRef<
  HTMLInputElement,
  FilledSearchInputProps
>(
  (
    {
      color,
      size,
      alwaysShowClear,
      className,
      containerClassName,
      hasSettingsIndicator,
      popover,
      onSettingsClick,
      onClear,
      ...inputProps
    },
    ref
  ) => {
    const { t } = useTranslations();

    const isClearButtonVisible =
      (onClear && inputProps.value) || alwaysShowClear;

    const optionsCount =
      (onSettingsClick ? 1 : 0) +
      (popover ? 1 : 0) +
      (isClearButtonVisible ? 1 : 0);

    const PADDING_CLASSES_BY_OPTIONS_COUNT: Record<number, string> = {
      0: "pr-4",
      1: "pr-12",
      2: "pr-20",
      3: "pr-28",
    };

    const paddingClassName =
      PADDING_CLASSES_BY_OPTIONS_COUNT[optionsCount] ?? "pr-4";

    return (
      <div className={cx("relative", containerClassName)}>
        <SearchIcon
          className={cx(
            "pointer-events-none absolute top-1/2 left-4 -translate-y-1/2",
            sizeToIconClasses[size]
          )}
        />
        <input
          type="text"
          placeholder={`${t({
            id: "common.search",
            defaultMessage: "Search",
          })}...`}
          className={cx(
            "w-full rounded-lg border-none pr-4 font-secondary leading-3 font-normal outline-none",
            paddingClassName,
            sizeToInputClasses[size],
            colorToClasses[color],
            className
          )}
          {...inputProps}
          ref={ref}
        />
        {(onSettingsClick || isClearButtonVisible || popover) && (
          <div
            className={cx(
              "absolute top-1/2 right-4 flex -translate-y-1/2 items-center gap-2.5"
            )}
          >
            {onSettingsClick && (
              <PlainButton onClick={onSettingsClick} className="relative">
                <SettingsIcon />
                {hasSettingsIndicator && (
                  <div className="absolute -top-px -right-px h-2 w-2 rounded-full border border-solid border-white bg-gradients-danger" />
                )}
              </PlainButton>
            )}
            {isClearButtonVisible && (
              <PlainButton>
                <ClearIcon onClick={onClear} />
              </PlainButton>
            )}
            {popover && <Popover {...popover} />}
          </div>
        )}
      </div>
    );
  }
);
